// src/components/Apm/Profile/AccountProfile.js

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import {
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import ButtonLoading from '../../Controls/Buttons/ButtonLoading';
import MultipleFileUploadField from '../../Upload/MultipleFileUploadField';

import { useUpdateCurrentUserMutation } from '../../../redux/features/authApi';

import { useGetImageQuery, useLazyGetImageQuery } from '../../../redux/features/imageApi';

//----------------------------------- UTILS -----------------------------------//

const getInitials = (firstName, lastName) => {
  return [firstName?.charAt(0), lastName?.charAt(0)].filter(Boolean).join('').toUpperCase();
};

//----------------------------------- COMPONENT -----------------------------------//

export const AccountProfile = ({ account }) => {
  const { t } = useTranslation();

  const imageId = account?.image ?? null;

  const {
    data: avatarResponse,
    isLoading: isAvatarLoading,
    isFetching: isAvatarFetching,
  } = useGetImageQuery(imageId, {
    skip: !imageId,
  });

  const [loadAvatar] = useLazyGetImageQuery();

  const [updateCurrentUser, { isLoading: isUpdatingCurrentUser }] = useUpdateCurrentUserMutation();

  const avatarUrl = avatarResponse?.s3_url ?? avatarResponse?.url ?? avatarResponse?.file_obj ?? '';

  const displayName = [account?.first_name, account?.last_name].filter(Boolean).join(' ');

  const initials = getInitials(account?.first_name, account?.last_name);

  /**
   * MultipleFileUploadField calls this after
   * uploading an image.
   */
  const getAvatar = useCallback(
    async (uploadedImageId) => {
      if (!uploadedImageId) {
        return null;
      }

      try {
        return await loadAvatar(uploadedImageId, true).unwrap();
      } catch (error) {
        console.error('Avatar loading failed:', error);

        return null;
      }
    },
    [loadAvatar]
  );

  /**
   * Save the uploaded image identifier
   * on the authenticated user.
   */
  const putAccountImage = useCallback(
    async (uploadedImageId) => {
      if (!account || !uploadedImageId) {
        toast.error(() => t('toast.not_saved_try_again'));

        return null;
      }

      try {
        const updatedAccount = await updateCurrentUser({
          image: uploadedImageId,
        }).unwrap();

        /*
         * updateCurrentUser invalidates Account/ME.
         * useRetrieveUserQuery will then receive
         * the updated image identifier.
         */
        toast.success(() => t('toast.avatar_successfully_uploaded'));

        return updatedAccount;
      } catch (error) {
        console.error('Avatar update failed:', error);

        console.error('Avatar update response:', error?.data);

        toast.error(() => t('toast.not_saved_try_again'), {
          autoClose: 4000,
        });

        return null;
      }
    },
    [account, t, updateCurrentUser]
  );

  /**
   * Clear the image reference from the user.
   *
   * This does not delete the ImageUpload row.
   * It only removes it from the current profile.
   */
  const deleteImage = useCallback(async () => {
    if (!account || !imageId) {
      return;
    }

    try {
      await updateCurrentUser({
        image: null,
      }).unwrap();

      toast.success(() => t('toast.avatar_successfully_deleted'));
    } catch (error) {
      console.error('Avatar deletion failed:', error);

      console.error('Avatar deletion response:', error?.data);

      toast.error(() => t('toast.not_deleted_try_again'), {
        autoClose: 4000,
      });
    }
  }, [account, imageId, t, updateCurrentUser]);

  const avatarLoading = isAvatarLoading || isAvatarFetching;

  return (
    <Card>
      <CardContent>
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{
            minHeight: 265,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: 140,
              height: 140,
              mb: 1,
            }}
          >
            <Avatar
              src={avatarUrl || undefined}
              alt={displayName || account?.email || ''}
              sx={{
                width: 140,
                height: 140,
                fontSize: 40,

                '& img': {
                  objectFit: 'cover',
                },
              }}
            >
              {!avatarUrl && initials}
            </Avatar>

            {avatarLoading && (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                }}
              >
                <CircularProgress size={28} />
              </Stack>
            )}
          </Box>

          <Typography color="text.primary" variant="h5" align="center">
            {displayName}
          </Typography>

          <Typography
            color="text.secondary"
            variant="subtitle1"
            align="center"
            sx={{
              overflowWrap: 'anywhere',
            }}
          >
            {account?.email}
          </Typography>
        </Stack>
      </CardContent>

      <Divider />

      <CardActions
        sx={{
          justifyContent: 'center',
          p: 2,
        }}
      >
        <MultipleFileUploadField account={account} putAccountImage={putAccountImage} getAvatar={getAvatar} />
      </CardActions>

      <Divider />

      <CardActions
        sx={{
          p: 2,
        }}
      >
        <ButtonLoading
          type="button"
          variant="outlined"
          color="secondary"
          fullWidth
          loading={isUpdatingCurrentUser}
          disabled={!imageId || isUpdatingCurrentUser}
          onClick={deleteImage}
        >
          {t('button.delete_image')}
        </ButtonLoading>
      </CardActions>
    </Card>
  );
};
